import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useUser, UserProvider } from './UserContext';
import Login from './Login';

// Every route below is fetched when it is opened. Imported eagerly, one visit to
// any page downloaded all of them, which is most of the bundle.
const Dashboard = lazy(() => import('./Dashboard'));
const Register = lazy(() => import('./Register'));
const SetPassword = lazy(() => import('./SetPassword'));
const Translate = lazy(() => import('./Translate'));
const Scanner = lazy(() => import('./Orders/Scanner.js'));
const Filetransfer = lazy(() => import('./filetransfer/Filetransfer.js'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const GeneriskDashboard = lazy(() => import('./PlatformTools/Generiskpro/GRLanding'));
const ReportView = lazy(() => import('./ReportViewer/ReportView'));
const OncoRisk = lazy(() => import('./WebServer/OncoRisk/oncorisk.js'));
const GeneRisk = lazy(() => import('./WebServer/Generisk/wsgenerisk.js'));
const FamilyRisk = lazy(() => import('./WebServer/FamilyRisk/FamilyRisk.js'));
const GVEP = lazy(() => import('./WebServer/GVEP/GVEP.js'));
const GPolygenesis = lazy(() => import('./WebServer/GPolygenesis/GPolygenesis.js'));
const GAtlas = lazy(() => import('./WebServer/GAtlas/GAtlas.js'));
const GPGS = lazy(() => import('./WebServer/GPGS/GPGS.js'));
const WBT = lazy(() => import('./WebServer/WBT/WBT.js'));
const ProteoRisk = lazy(() => import('./WebServer/ProteoRisk/ProteoRisk.js'));
const ProteoRiskAbout = lazy(() => import('./WebServer/ProteoRisk/ProteoRiskAbout.js'));
const HPA = lazy(() => import('./HPAGFrontend/HPA.js'));
const AdminAnalytics = lazy(() => import('./HPAGFrontend/AdminAnalytics.js'));
const NewGenome = lazy(() => import('./WebServer/NewGenome/NewGenome.js'));
const UltimateHome = lazy(() => import('./UltimateHome/UltimateHome.js'));
const OmicAtlas = lazy(() => import('./OmicsAtlas/OmicAtlas.js'));
const Monad = lazy(() => import('./MONAD/App'));
const AnalyticsPlatform = lazy(() => import('./Analytics/AnalyticsPlatform.js'));
const Xomics = lazy(() => import('./xomics/Xomics.js'));
const Clinician = lazy(() => import('./clinician/clinician.js'));
const GeneRiskAbout = lazy(() => import('./UltimateHome/components/about/GeneRiskAbout.js'));
const FamilyRiskAbout = lazy(() => import('./UltimateHome/components/about/FamilyRiskAbout.js'));
const OncoRiskAbout = lazy(() => import('./UltimateHome/components/about/OncoRiskAbout.js'));
const VarXOmicsAbout = lazy(() => import('./UltimateHome/components/about/VarXOmicsAbout.js'));
const GVEPAbout = lazy(() => import('./UltimateHome/components/about/GVEPAbout.js'));
const MobileAppAbout = lazy(() => import('./UltimateHome/components/about/MobileAppAbout.js'));
const Meeting = lazy(() => import('./clinician/Meeting.js'));

// Kits are activated on the store's own page, which records them in XOmics. The old page here wrote to the old
// system, so the new app never saw those kits.
const KIT_ACTIVATION_URL = 'https://phenomelongevity.com/account/activate/start/';

function ExternalRedirect({ to }) {
    React.useEffect(() => {
        window.location.replace(to);
    }, [to]);
    return null;
}

// A route whose chunk never arrives would otherwise render nothing at all. That
// is usually a stale reference after a deploy, which reloading fixes.
class RouteBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    render() {
        if (!this.state.failed) return this.props.children;
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>This page could not be loaded.</p>
                <button type="button" onClick={() => window.location.reload()}>Reload</button>
            </div>
        );
    }
}

function App() {
    return (
        <UserProvider>
            <Router>
                <RouteBoundary>
                <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }} />}>
                <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<UltimateHome />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/oncorisk" element={<OncoRisk />} />
                    <Route path="/varxomics" element={<NewGenome />} />
                    <Route path="/gvep" element={<GVEP />} />
                    <Route path="/gpolygenesis" element={<GPolygenesis />} />
                    <Route path="/ggwas" element={<GAtlas />} />
                    <Route path="/gpgs" element={<GPGS />} />
                    <Route path="/wbtrisk" element={<WBT />} />
                    <Route path="/wbt" element={<Navigate to="/wbtrisk" replace />} />
                    <Route path="/proteorisk/*" element={<ProteoRisk />} />

                    <Route path="/familyrisk" element={<FamilyRisk />} />

                    <Route path="/genriskpro" element={<GeneRisk />} />
                    <Route path="/babygenepro" element={<GeneriskDashboard />} />
                    <Route path="/hpa" element={<HPA />} />
                    <Route path="/hpa-admin" element={<AdminAnalytics />} />
                    <Route path="/analytics" element={<AnalyticsPlatform />} />

                    {/* About page routes */}
                    <Route path="/xomics/*" element={<Xomics />} />
                    <Route path="/clinicportal/*" element={
                        <ProtectedRoute>
                            <Clinician />
                        </ProtectedRoute>
                    } />
                    <Route path="/genriskpro/about" element={<GeneRiskAbout />} />
                    <Route path="/familyrisk/about" element={<FamilyRiskAbout />} />
                    <Route path="/oncorisk/about" element={<OncoRiskAbout />} />
                    <Route path="/varxomics/about" element={<VarXOmicsAbout />} />
                    <Route path="/gvep/about" element={<GVEPAbout />} />
                    <Route path="/proteorisk/about" element={<ProteoRiskAbout />} />
                    <Route path="/mobileapp/about" element={<MobileAppAbout />} />
                    <Route path="/oa" element={<MobileAppAbout />} />
                    <Route path="/hpaagent" element={<MobileAppAbout />} />

                    <Route path="/report/:uuid" element={<ReportView />} />
                    <Route path="/set-password" element={<SetPassword />} />
                    <Route path="/activate" element={<ExternalRedirect to={KIT_ACTIVATION_URL} />} />
                    <Route path="/active" element={<ExternalRedirect to={KIT_ACTIVATION_URL} />} />

                    <Route path="/omics" element={<OmicAtlas />} />
                    <Route path="/monad" element={<Monad />} />
                    <Route path="/meeting/:session_uuid" element={<Meeting />} />

                    {/* Protected routes */}
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/filetransfer1" element={
                        <ProtectedRoute>
                            <Filetransfer />
                        </ProtectedRoute>
                    } />
                    <Route path="/translate/:lang" element={<Translate />} />
                    <Route path="/ScanEx" element={<Scanner />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    {/* Add any other protected routes here */}
                </Routes>
                </Suspense>
                </RouteBoundary>
            </Router>
        </UserProvider>
    );
}

const ProtectedRoute = ({ children }) => {
    const { user, isLoadingUser } = useUser();
    const token = Cookies.get('token');

    // Wait if user data is still loading
    if (isLoadingUser) {
        // Optionally, return a loading spinner here instead of null
        return null;
    }

    // Check both user state from context and token cookie after loading is complete
    if (!user || !token) {
        console.log('[ProtectedRoute] Redirecting to login. User state exists:', !!user, 'Token exists:', !!token);
        return <Navigate to="/login" />;
    }

    return children;
};

export default App;
